import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import { INK, MUTED_36, MUTED_60, MUTED_45, FONT as YFONT, ROOMS, glow, capsuleStyle,
         AMBIENT_LIBRARY, NEBULA_LIBRARY } from "@/lib/yuoDesign";
import RoomShell from "@/components/yuo/RoomShell";
import { useResponsive } from "@/lib/responsive";
import StoneOrb from "@/components/yuo/StoneOrb";

export default function Library() {
  const { isPhone } = useResponsive();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [activeSource, setActiveSource] = useState("bible");
  const [testamentFilter, setTestamentFilter] = useState("ALL");
  const [limits, setLimits] = useState(null);
  const [breakdowns, setBreakdowns] = useState([]);
  const [tab, setTab] = useState("scriptures"); // "scriptures" | "notes"

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [full, lim, bs] = await Promise.all([
          api.fullBooks(), api.libraryLimits(), api.listBreakdowns(),
        ]);
        setSources(full?.sources || []);
        setLimits(lim);
        setBreakdowns(bs?.breakdowns || []);
      } catch (e) { console.warn(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const active = useMemo(() => sources.find((s) => s.source === activeSource), [sources, activeSource]);
  const books = useMemo(() => {
    if (!active) return [];
    if (activeSource === "bible") {
      const all = active.books || [];
      if (testamentFilter === "ALL") return all;
      return all.filter((b) => b.testament === testamentFilter);
    }
    return active.books || [];
  }, [active, activeSource, testamentFilter]);

  const openBook = (b) => {
    let chapter = 1;
    if (activeSource === "aurelius") chapter = b.book_num || 1;
    else if (activeSource === "quran") chapter = b.book_num || 1;
    else if (["tao_te_ching","dhammapada","bhagavad_gita"].includes(activeSource))
      chapter = b.chapter || b.book_num || 1;
    navigate(`/library/read?source=${activeSource}&code=${b.code}&chapter=${chapter}&lang=en`);
  };

  const planLabel = limits?.plan === "master" ? "Master" : limits?.plan === "pro" ? "Pro" : "Free";
  const limitLabel = limits && limits.limit == null ? "unlimited breakdowns"
    : limits ? `${limits.used} / ${limits.limit} breakdowns today` : "";

  return (
    <RoomShell
      room="library"
      testId="library-screen"
      ambient={AMBIENT_LIBRARY}
      nebula={NEBULA_LIBRARY}
      hideHeaderStone={true}
      title={undefined}
    >
      {/* Hero — huge YUO stone at top */}
      <div style={{
        display: "flex", flexDirection: "column", alignItems: "center",
        gap: 18, padding: "20px 0 30px", textAlign: "center",
      }}>
        <StoneOrb color="symbiosis" size={isPhone ? 72 : 88} facets dust darkHalo title="YUO — cosmic beginning" />
        <div style={{ color: "var(--lit-green-6)", fontSize: 10, letterSpacing: "0.34em", textTransform: "uppercase" }}>
          Library of meanings
        </div>
        <div style={{ color: INK, fontSize: isPhone ? 30 : 34, letterSpacing: -0.4, lineHeight: 1.2 }}>Living texts</div>
        <div style={{ color: MUTED_60, fontSize: 12.5, lineHeight: 1.6, maxWidth: 620 }}>
          The full Bible (RU · EN · PT), the Torah (EN · RU · HE), the Qur'an (EN · RU · AR),
          Marcus Aurelius' Meditations and Eastern classics. Read chapters and verses in whole —
          when you wish, ask Yuo…theologian for a breakdown.
        </div>
      </div>

      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        {/* Tabs — Scriptures / Notes */}
        <div style={{ display: "flex", gap: 10, marginBottom: 22, alignItems: "center", flexWrap: "wrap" }}>
          <button data-testid="library-tab-scriptures" onClick={() => setTab("scriptures")}
            style={capsuleStyle({ active: tab === "scriptures", room: "library" })}>
            Scriptures
          </button>
          <button data-testid="library-tab-notes" onClick={() => setTab("notes")}
            style={capsuleStyle({ active: tab === "notes", room: "library" })}>
            Notes{breakdowns.length ? ` · ${breakdowns.length}` : ""}
          </button>
        </div>

        {tab === "notes" ? (
          breakdowns.length === 0 ? (
            <div data-testid="library-notes-empty"
              style={{ padding: 40, textAlign: "center", color: MUTED_36, fontSize: 12, letterSpacing: "0.24em", textTransform: "uppercase" }}>
              no notes yet · ask Yuo…theologian inside a chapter
            </div>
          ) : (
            <div data-testid="library-my-reflections" style={{ display: "grid", gap: 10 }}>
              {breakdowns.map((b) => (
                <div key={b.breakdown_id} data-testid={`saved-breakdown-${b.breakdown_id}`}
                  style={{
                    padding: 22, borderRadius: 26, border: "none",
                    background: glow(ROOMS.library.rgb, 0.10),
                    color: INK, fontFamily: YFONT,
                    animation: "yuo-emerge .9s cubic-bezier(.2,.7,.2,1) both",
                  }}>
                  <div style={{ color: ROOMS.library.accent, fontSize: 12, letterSpacing: 0.2, marginBottom: 6 }}>
                    {b.ref_human || b.ref}
                  </div>
                  {b.verse_text && (
                    <div style={{ color: MUTED_60, fontStyle: "italic", fontSize: 12, marginBottom: 6, lineHeight: 1.4 }}>
                      «{b.verse_text}»
                    </div>
                  )}
                  <div style={{
                    color: INK, fontSize: 13, lineHeight: 1.55,
                  }}>{b.breakdown}</div>
                  <div style={{ color: MUTED_36, fontSize: 10, marginTop: 8, letterSpacing: 0.2 }}>
                    {new Date(b.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
        <>
        {/* Verse of the Day — amber card with an amber orb on the right */}
        <button data-testid="library-daily-triad-cta" onClick={() => navigate("/library/daily")}
          style={{
            width: "100%", padding: "22px 26px", borderRadius: 30, border: "none",
            background: glow(ROOMS.library.rgb, 0.16),
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 20, textAlign: "left", cursor: "pointer", marginBottom: 22,
            animation: "yuo-emerge 1s cubic-bezier(.2,.7,.2,1) both",
          }}>
          <div>
            <div style={{ color: ROOMS.library.accent, fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase", marginBottom: 6 }}>
              Verse of the day · Three Scriptures
            </div>
            <div style={{ color: INK, fontSize: 16, letterSpacing: -0.2, marginBottom: 4 }}>
              Bible · Torah · Qur'an
            </div>
            <div style={{ color: MUTED_45, fontSize: 12 }}>One screen · refreshes every morning</div>
          </div>
          <StoneOrb color="library" size={44} />
        </button>

        {/* Plan row */}
        {limits && (
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 12, padding: "10px 22px", borderRadius: 999,
            background: "var(--fg-05)", marginBottom: 22,
          }}>
            <div style={{ color: MUTED_60, fontSize: 12, letterSpacing: 0.2 }}>
              <span style={{ color: ROOMS.library.accent, fontWeight: 600 }}>{planLabel}</span>
              {" · "}
              {limitLabel}
            </div>
            {limits.plan !== "master" && (
              <button data-testid="library-upgrade-link" onClick={() => navigate("/paywall")}
                style={capsuleStyle({ active: true, room: "library", size: "sm" })}>
                Upgrade
              </button>
            )}
          </div>
        )}

        {/* Scripture picker — 4 columns */}
        <div style={{
          display: "grid", gap: isPhone ? 10 : 12, gridTemplateColumns: isPhone ? "repeat(2, minmax(0,1fr))" : "repeat(auto-fill, minmax(220px, 1fr))",
          marginBottom: 20,
        }}>
          {sources.map((sObj) => {
            const s = sObj.source;
            const focused = activeSource === s;
            const label =
              s === "bible" ? "The Bible" :
              s === "torah" ? "Torah · תּוֹרָה" :
              s === "quran" ? "The Qur'an · ٱلْقُرْآن" :
              s === "aurelius" ? "Marcus Aurelius" :
              s === "tao_te_ching" ? "Tao Te Ching · 道德經" :
              s === "dhammapada" ? "Dhammapada · धम्मपद" :
              s === "bhagavad_gita" ? "Bhagavad Gita · श्रीमद्भगवद्गीता" :
              (sObj.title_en || s);
            const sub =
              s === "bible" ? "66 books · Synodal / KJV / Almeida" :
              s === "torah" ? "5 books · English · Russian · Hebrew" :
              s === "quran" ? "114 surahs · English · Russian · Arabic" :
              s === "aurelius" ? "12 books · Meditations" :
              s === "tao_te_ching" ? "81 chapters · English · Chinese" :
              s === "dhammapada" ? "26 chapters · English · Pali" :
              s === "bhagavad_gita" ? "18 chapters · English · Sanskrit" :
              "";
            return (
              <button key={s} data-testid={`library-source-${s}`} onClick={() => setActiveSource(s)}
                style={{
                  padding: "18px 20px", borderRadius: 26, border: "none",
                  background: glow(ROOMS.library.rgb, focused ? 0.24 : 0.10),
                  boxShadow: focused ? `0 0 32px rgba(${ROOMS.library.rgb},0.32)` : "none",
                  cursor: "pointer", textAlign: "left", color: INK, fontFamily: YFONT,
                }}>
                <div style={{ color: focused ? ROOMS.library.hex : INK, fontSize: 15, letterSpacing: -0.2, lineHeight: 1.3 }}>
                  {label}
                </div>
                <div style={{ color: MUTED_45, fontSize: 10.5, marginTop: 5, letterSpacing: 0.18 }}>{sub}</div>
              </button>
            );
          })}
        </div>

        {activeSource === "bible" && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {[{k:"ALL",l:"All 66"},{k:"OT",l:"Old Testament"},{k:"NT",l:"New Testament"}].map((f) => (
              <button key={f.k} data-testid={`library-filter-${f.k}`}
                onClick={() => setTestamentFilter(f.k)}
                style={capsuleStyle({ active: testamentFilter === f.k, room: "library", size: "sm" })}>
                {f.l}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: MUTED_36, fontSize: 11, letterSpacing: "0.3em", textTransform: "uppercase" }}>
            opening…
          </div>
        ) : (
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: isPhone ? "1fr" : "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {books.map((b) => (
              <button key={`${activeSource}-${b.code}-${b.index}`} data-testid={`library-book-${b.code}`}
                onClick={() => openBook(b)}
                style={{
                  padding: "16px 16px", borderRadius: 24, border: "none",
                  background: glow(ROOMS.library.rgb, 0.10),
                  minHeight: 96, display: "flex", flexDirection: "column",
                  justifyContent: "space-between", textAlign: "left",
                  color: INK, fontFamily: YFONT, cursor: "pointer",
                }}>
                <div>
                  <div style={{ color: INK, fontSize: 14, lineHeight: 1.3 }}>{b.name_en || b.name_ru}</div>
                  <div style={{ color: MUTED_45, fontSize: 9.5, letterSpacing: 0.18, marginTop: 4 }}>
                    {activeSource === "torah" && b.name_he ? b.name_he
                      : activeSource === "quran" && b.name_ar ? b.name_ar
                      : activeSource === "aurelius" ? `Book ${b.book_num}`
                      : ["tao_te_ching","dhammapada","bhagavad_gita"].includes(activeSource) ? (b.chapter_title || "")
                      : (b.testament_en || b.testament || "")}
                  </div>
                </div>
                <div style={{ color: ROOMS.library.accent, fontSize: 11, marginTop: 10 }}>
                  {activeSource === "quran" ? `${b.ayah_count || "?"} ayahs${b.revelation ? ` · ${b.revelation}` : ""}`
                    : activeSource === "aurelius" ? `Book ${b.book_num}`
                    : activeSource === "tao_te_ching" ? `Chapter ${b.chapter}`
                    : ["dhammapada","bhagavad_gita"].includes(activeSource) ? `${b.verse_count || "?"} verses`
                    : `${b.chapter_count} chapters`}
                </div>
              </button>
            ))}
          </div>
        )}

        {breakdowns.length > 0 && tab === "scriptures" && (
          <div style={{ marginTop: 40 }} data-testid="library-my-reflections-inline">
            <div style={{ color: ROOMS.library.accent, fontSize: 11, letterSpacing: "0.32em", textTransform: "uppercase", marginBottom: 14 }}>
              Notes · open Notes tab to see all
            </div>
          </div>
        )}
        </>
        )}
      </div>

      {/* MyNotesSheet retained for other rooms that still open it via router — kept invisible here */}
    </RoomShell>
  );
}
